import React from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { Link, Navigate, Outlet, useLocation } from "react-router";
import { Page, Tabs } from "@sscs/ui";
import {
  FORBIDDEN_ROUTE,
  GROUPS_ROUTE,
  MEMBERS_ROUTE,
  PERMISSION,
} from "Constants";
import { useAllowedTo, useCurrentOrganization } from "Hooks";
import { useGetOrganizationQuery } from "../../services/organizations.api";

const MembersPage = (): React.JSX.Element => {
  // viewing members is an edge case that mainly affects the trial instance
  // when it's a member route and the user has no permissions, fall back
  const canViewMembers = useAllowedTo({
    permissions: [PERMISSION.VIEW_ALL_ORGANIZATION_MEMBERS],
  });
  const canManageMembers = useAllowedTo({
    permissions: [PERMISSION.MODIFY_MEMBERS_ORGANIZATION_ROLE],
  });

  const currentOrganization = useCurrentOrganization();
  const { pathname } = useLocation();
  const { data: organizationData } = useGetOrganizationQuery(
    currentOrganization
      ? { organizationId: currentOrganization.id }
      : skipToken,
  );

  if (!canViewMembers) {
    return <Navigate to={`/${FORBIDDEN_ROUTE}`} replace />;
  }

  const activeTab = pathname.includes(`/${GROUPS_ROUTE}`)
    ? GROUPS_ROUTE
    : MEMBERS_ROUTE;
  const memberCount = organizationData?.member_count;
  const groupCount = organizationData?.group_count;

  return (
    <Page.Root>
      <Page.Header
        title={`${canManageMembers ? "Manage" : "View"} Groups/Members`}
      />
      <Page.Tabs>
        <Tabs.Tabs value={activeTab}>
          <Tabs.Tab
            id="members-tab"
            value={MEMBERS_ROUTE}
            label="Members"
            count={memberCount}
            to="."
            component={Link}
          />
          <Tabs.Tab
            id="groups-tab"
            value={GROUPS_ROUTE}
            label="Groups"
            count={groupCount}
            to={GROUPS_ROUTE}
            component={Link}
          />
        </Tabs.Tabs>
      </Page.Tabs>
      <Outlet />
    </Page.Root>
  );
};

export default MembersPage;
